import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Divider} from 'antd';
import _ from 'lodash';
import {useDispatch} from 'react-redux';
import {t} from '../../../../../../services/i18n';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import VStack from '../../../../../common/Layout/VStack';
import AppText from '../../../../../common/AppText';
import FiltersHandler from '../../../../../common/FiltersHandler';
import usePermissions from '../../../../../../services/Permissions';
import {fetchApi} from '../../../../../../services/HTTP';
import {updateTempSelection} from '../../../../../../redux/stepperSlice';
import ChooseHugToSwapSkeleton from '../Steps/ChooseHugToSwapSkeleton';
import {getTrackCourseFiltersConfig} from './trackCourseFiltersConfig';
import CourseFromTrack from './CourseFromTrack';
import {
    getTrackCoursesSelection,
    groupTrackCoursesByCategory,
    isEventChecked,
} from './trackCourseSelectionHelpers';

const ChooseTrackCourses = ({values, setFieldValue, target}) => {
    const dispatch = useDispatch();
    const {has} = usePermissions();
    const track = values?.track;
    const userId = target?.user?.id ?? target?.user?.user_fk;
    const [isLoading, setIsLoading] = useState(false);
    const [filteredCourses, setFilteredCourses] = useState([]);
    const [trackCourses, setTrackCourses] = useState(null);

    const showCategoryGroupFilter = useMemo(
        () => has('boxCategoriesGroups') && !has('boxCategoriesGroupsLock'),
        [has]
    );

    const filtersConfig = useMemo(
        () => getTrackCourseFiltersConfig({showCategoryGroupFilter}),
        [showCategoryGroupFilter]
    );
    const filterList = useMemo(
        () => trackCourses?.map(trackCourse => trackCourse.series),
        [trackCourses]
    );
    const groupedCourses = useMemo(
        () => groupTrackCoursesByCategory(filteredCourses),
        [filteredCourses]
    );

    const fetchTrackCourses = useCallback(async () => {
        if (!track?.id) {
            setTrackCourses(null);
            return;
        }
        try {
            setIsLoading(true);
            const res = await fetchApi(
                'seriesTrack/getTrackCoursesForRegistration',
                'POST',
                {series_track_id: track.id, userId},
                false,
                true,
            );
            setTrackCourses(res ?? []);
        } catch (e) {
            console.error(e);
            setTrackCourses([]);
        } finally {
            setIsLoading(false);
        }
    }, [track?.id, userId]);

    useEffect(() => {
        fetchTrackCourses();
    }, [fetchTrackCourses]);

    const trackCoursesSelection = useMemo(
        () => getTrackCoursesSelection({
            courses: values.courses,
            trackCourses,
            trackSessionPerWeek: track?.session_per_week,
        }),
        [values.courses, trackCourses, track?.session_per_week],
    );

    useEffect(() => {
        if (isLoading || trackCourses === null) {
            dispatch(updateTempSelection({canProceed: false}));
            return;
        }
        dispatch(updateTempSelection({canProceed: trackCoursesSelection.canProceed}));
    }, [trackCoursesSelection.canProceed, isLoading, trackCourses, dispatch]);

    useEffect(() => {
        return () => dispatch(updateTempSelection(null));
    }, [dispatch]);

    useEffect(() => {
        if (isLoading || trackCourses === null) {
            return;
        }
        if (!trackCourses.length && (values.courses ?? []).length) {
            setFieldValue('courses', [], false);
        }
    }, [trackCourses, isLoading]);

    useEffect(() => {
        if (isLoading || trackCourses === null) {
            return;
        }
        setFieldValue('trackCoursesSelection', trackCoursesSelection, false);
    }, [trackCoursesSelection, isLoading, trackCourses]);

    const onFiltered = (filteredSeries) => {
        setFilteredCourses(trackCourses?.filter(trackCourse =>
            filteredSeries.some(series => series.id === trackCourse.series_id)
        ));
    };

    const onSelectSeriesEvent = (seriesId, eventId) => {
        const seriesExistingSelection = (values.courses ?? []).find(course => course.series_id === seriesId);
        const isAlreadySelected = isEventChecked(values.courses, seriesId, eventId);

        if (isAlreadySelected) {
            const updatedEventIds = (seriesExistingSelection.series_event_ids ?? []).filter(id => id !== eventId);
            const updatedCourses = (values.courses ?? [])
                .map(course =>
                    course.series_id === seriesId
                        ? {...course, series_event_ids: updatedEventIds}
                        : course
                )
                .filter(course => (course.series_event_ids?.length ?? 0) > 0);
            return setFieldValue('courses', updatedCourses);
        }

        if (!seriesExistingSelection) {
            return setFieldValue('courses', [...(values.courses ?? []), {series_id: seriesId, series_event_ids: [eventId]}]);
        }

        const updatedEventIds = [...(seriesExistingSelection.series_event_ids ?? []), eventId];
        const updatedCourses = (values.courses ?? []).map(course =>
            course.series_id === seriesId
                ? {...course, series_event_ids: updatedEventIds}
                : course
        );

        setFieldValue('courses', updatedCourses);
    };

    if (isLoading) return <ChooseHugToSwapSkeleton />;

    if (!trackCourses?.length) {
        return (
            <AppText fontSize={GlobalStyleAttributes.fonts.standard16} style={{padding: '2rem 0'}}>
                {t('no-activities-in-track')}
            </AppText>
        );
    }

    return (
        <VStack space={'1.5rem'}>
            <FiltersHandler
                list={filterList}
                config={filtersConfig}
                onFiltered={onFiltered}
            />
            <VStack space={'1.25rem'}>
                <Divider style={{margin: 0}} />
                {Object.entries(groupedCourses).map(([categoryName, coursesInCategory]) => (
                    <VStack key={categoryName || 'uncategorized'} space={'1.25rem'}>
                        {categoryName && (
                            <AppText fontSize={GlobalStyleAttributes.fonts.heading2Desktop} boldFont>
                                {categoryName}
                            </AppText>
                        )}
                        <VStack space={'1rem'}>
                            {coursesInCategory.map((course, idx) => (
                                <React.Fragment key={course.series_id}>
                                    <CourseFromTrack
                                        course={course}
                                        courses={values.courses ?? []}
                                        trackSessionPerWeek={track?.session_per_week}
                                        onSelect={onSelectSeriesEvent}
                                    />
                                    <Divider style={{margin: 0}} />
                                </React.Fragment>
                            ))}
                        </VStack>
                    </VStack>
                ))}
                {Object.keys(groupedCourses).length === 0 && (
                    <AppText fontSize={GlobalStyleAttributes.fonts.standard16} style={{padding: '2rem 0'}}>
                        {t('no-hugim-found')}
                    </AppText>
                )}
            </VStack>
        </VStack>
    );
};

export default ChooseTrackCourses;
